/* eslint-disable eslint-comments/no-unlimited-disable */
import { Text, View } from '@gluestack-ui/themed'
import isEmpty from 'lodash/isEmpty'
import React, { FunctionComponent, useState } from 'react'
import { ImageSourcePropType, StyleSheet, ViewStyle } from 'react-native'
import Animated, { Extrapolation, interpolate, runOnJS, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue } from 'react-native-reanimated'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'
import { theme } from '../../theme'
import { Title } from '../NativeBaseLegacy'
import { HeaderCarrousel } from './components/HeaderCarrousel.component'
import { HeaderImage } from './components/HeaderImage.component'
import { HeaderMovie } from './components/HeaderMovie.component'

const MAX_HEIGHT = theme.deviceHeight / 2.7
const MIN_HEIGHT = 55

interface HeaderScrollProps {
  title: string
  foregroundImage?: string | null
  images: string[]
  left?: React.ReactNode
  leftHeader?: React.ReactNode
  right?: React.ReactNode
  imageDefault: ImageSourcePropType
  styleTitle?: ViewStyle
  children?: React.ReactNode
}

export const HeaderScrollView: FunctionComponent<HeaderScrollProps> = ({ children, foregroundImage, images, imageDefault, title, left, right, styleTitle }) => {
  const [displayBottom, setDisplayBottom] = useState(true)
  const safearea = useSafeAreaInsets()
  const headerMin = MIN_HEIGHT + safearea.top
  const collapseRange = Math.max(1, MAX_HEIGHT - headerMin)
  const scrollY = useSharedValue(0)

  const updateDisplayBottom = (visible: boolean) => setDisplayBottom(prev => (prev === visible ? prev : visible))

  const scrollHandler = useAnimatedScrollHandler(event => {
    scrollY.value = event.contentOffset.y
    runOnJS(updateDisplayBottom)(event.contentOffset.y < collapseRange * 0.5)
  })

  const imageStyle = useAnimatedStyle(() => ({
    // parallax : l'image défile à ~moitié vitesse
    transform: [{ translateY: interpolate(scrollY.value, [0, MAX_HEIGHT], [0, MAX_HEIGHT * 0.4], Extrapolation.CLAMP) }],
  }))

  const fixedTitleStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollY.value, [collapseRange - 60, collapseRange], [0, 1], Extrapolation.CLAMP),
  }))

  const renderImage = () => {
    if (foregroundImage) {
      const firstImage = images[0]
      const fbImage = firstImage ? foregroundImage : undefined
      const bgImage = firstImage || foregroundImage
      return <HeaderMovie foregroundImage={fbImage} backgroundImage={bgImage} left={left} right={right} displayBottom={displayBottom} maxHeight={MAX_HEIGHT} />
    }
    if (!isEmpty(images)) {
      if (images.length > 1) {
        return <HeaderCarrousel images={images} maxHeight={MAX_HEIGHT} left={left} displayBottom={displayBottom} />
      }
      return <HeaderImage fastImage={images[0]} left={left} displayBottom={displayBottom} maxHeight={MAX_HEIGHT} />
    }

    return <HeaderImage image={imageDefault} left={left} displayBottom={displayBottom} maxHeight={MAX_HEIGHT} />
  }

  return (
    <View style={styles.contentFlex}>
      <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator={false}>
        <Animated.View style={[styles.imageWrap, imageStyle]}>{renderImage()}</Animated.View>
        <SafeAreaView>
          <View style={styles.section}>
            <Text style={[styles.title, styleTitle]}>{title}</Text>
          </View>
          <View style={styles.viewChidrens}>{children}</View>
        </SafeAreaView>
      </Animated.ScrollView>

      <View style={[styles.fixedBar, { height: headerMin, paddingTop: safearea.top }]} pointerEvents="none">
        <Animated.View style={[styles.fixedBarTitle, fixedTitleStyle]}>
          <Title style={styles.headerTitle}>{title}</Title>
        </Animated.View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  contentFlex: {
    backgroundColor: theme.colors.white,
    height: theme.deviceHeight,
  },
  imageWrap: {
    height: MAX_HEIGHT,
    overflow: 'hidden',
  },
  fixedBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  fixedBarTitle: {
    ...StyleSheet.absoluteFill,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.header.titleSize,
    color: theme.colors.white,
  },
  title: {
    fontSize: theme.textSize.x3,
    lineHeight: theme.textSize.x3,
    textAlign: 'center',
    width: '100%',
  },
  section: {
    paddingHorizontal: theme.padding.x2,
    textAlign: 'center',
    backgroundColor: theme.colors.transparent,
  },
  viewChidrens: {},
})
